import React, { useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, EffectFade } from 'swiper/modules';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

import aboutSectionPhoto from '../assets/about_section.png';
import aboutUsPhoto from '../assets/hero_img_page.png';
import visionPhoto from '../assets/vision.png';
import missionPhoto from '../assets/mission.png';
import teamPhoto from '../assets/nd_industry_team.png';
import expansionPhoto from '../assets/nd_expansion.png';

// Legacy Timeline Photos
import legacyPhoto1 from '../assets/nd_legacy_heritage.png';
import legacyPhoto2 from '../assets/nd_mission_craftsmanship.png';
import legacyPhoto3 from '../assets/nd_vision_visionary_tools.png';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faBullseye, faEye } from '@fortawesome/free-solid-svg-icons';

import useBidirectionalScroll from '../hooks/useBidirectionalScroll';

export default function AboutPage({ navigateTo }) {
  const [activeTab, setActiveTab] = useState(0);
  const swiperRef = useRef(null);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useBidirectionalScroll('.about-page-container');

  const timelineData = [
    { year: '2005', subtitle: 'Foundation', title: 'The Starting Point', desc: 'Inception of N.D. Industry in Rajkot. We started with a vision to provide high-quality manual tools for local jewellery artisans.', backImg: legacyPhoto1, frontImg: teamPhoto },
    { year: '2011', subtitle: 'Modernization', title: 'Engineered Growth', desc: 'Shifted focus towards semi-automated jewellery machinery, significantly reducing production time for gold smiths.', backImg: legacyPhoto2, frontImg: missionPhoto },
    { year: '2016', subtitle: 'Expansion', title: 'National Presence', desc: 'Celebrating a decade of excellence. N.D. Tools expanded its reach across India, becoming a trusted name nationwide.', backImg: aboutSectionPhoto, frontImg: expansionPhoto },
    { year: '2021', subtitle: 'Digital Era', title: 'Digital Revolution', desc: 'Launched next-gen digital precision tools, integrating advanced calibration and micro-level accuracy.', backImg: legacyPhoto3, frontImg: visionPhoto },
    { year: '2026', subtitle: 'The Future', title: 'Industry 4.0', desc: 'Leading the future with smart workshop solutions and AI-integrated machinery, redefining jewellery making.', backImg: teamPhoto, frontImg: legacyPhoto3 }
  ];

  const handleTabClick = (index) => {
    setActiveTab(index);
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  return (
    <div className="about-page-container" style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>

      {/* Hero Section */}
      <section className="hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* 3D Interactive Parallax Background Typography */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: 'min(20vw, 250px)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(10, 102, 255, 0.08)',
            letterSpacing: '10px',
            transform: `translate(${mousePos.x * 50}px, ${mousePos.y * 50}px)`,
            transition: 'transform 0.1s ease-out',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            ABOUT
          </div>
        </div>

        {/* Ambient Grid Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(10, 102, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 102, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
          transition: 'transform 0.15s ease-out',
        }}></div>

        <div className="hero-background-slider" style={{ zIndex: 2, transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)`, transition: 'transform 0.2s ease-out' }}>
          <div className="full-slide active">
            <div className="bg-img-container" style={{ position: 'absolute', inset: 0 }}>
              <img src={aboutUsPhoto} alt="About N.D. Industry" className="bg-img" style={{ objectPosition: 'right center' }} />
            </div>
            <div className="bg-overlay"></div>
          </div>
        </div>

        <div className="hero-content" style={{ zIndex: 10 }}>
          <button className="outline-button" style={{ marginBottom: '2rem' }} onClick={() => navigateTo('home')}>
            &#8592; Back to Home
          </button>
          <h1 className="hero-title">About Us</h1>
          <p className="hero-description">
            <strong style={{ color: "var(--accent-color)" }}>Since 2005</strong><br /><br />
            Discover our legacy of manufacturing excellence and commitment to high-quality industrial solutions.
          </p>
        </div>
      </section>

      {/* Trust & Quality Section */}
      <section className="about-section" style={{ padding: '8rem 6rem', background: 'var(--bg-primary)' }}>
        <div className="about-content">
          <div className="about-text animate-hidden animate-left">
            <div className="hero-badge" style={{ marginBottom: "1rem" }}>A Decade of Trust & Quality</div>
            <h2 className="section-title">The Standard of <span style={{ color: 'var(--accent-color)' }}>Jewellery <br />Engineering</span></h2>
            <p className="about-description">
              ND Industry Is One Of The Leading Company In Manufacturing And Supplying
              All Type Of Jewellery Tools And Machinery In Rajkot (Guj.)
              In Just A Short Period Of About 21 Years, We Became Most Trusted Brand In Terms
              Of Quality And Service We Offer. Currently We Have Vast Range Of Machines And Tools
              Used For Jewellery Making Process I.e : Wire & Sheet Machine From Small Size To Large
              Size, Electric & Gas Melting Furnace, Vibrator Machine, Electric Vaccume Polisher, Etc...
              We Are Currently Supplying Our Products To Many States Of India
            </p>
          </div>
          <div className="about-image-wrapper animate-hidden animate-right delay-200" style={{ position: 'relative' }}>
            {/* Corner Decorative Circles */}
            <div style={{
              position: 'absolute',
              bottom: '-40px',
              left: '-40px',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'rgba(10, 102, 255, 0.15)',
              zIndex: 0,
              pointerEvents: 'none'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-15px',
              left: '-15px',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'var(--accent-color)',
              zIndex: 0,
              opacity: 0.8,
              pointerEvents: 'none'
            }}></div>
            <img src={aboutSectionPhoto} alt="Facility" className="about-image" style={{ position: 'relative', zIndex: 1 }} />
          </div>
        </div>
      </section>

      <div className="molten-divider-wrapper">
        <svg className="molten-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <defs>
            <linearGradient id="liquid-silver" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f4f9ff" />
              <stop offset="50%" stopColor="#dbeafe" />
              <stop offset="100%" stopColor="#0b1f3a" />
            </linearGradient>
          </defs>
          <path
            fill="url(#liquid-silver)"
            d="M0,160 C320,300,420,40,720,160 C1020,280,1120,60,1440,200 L1440,320 L0,320 Z"
          ></path>
        </svg>
      </div>

      {/* PREMIUM INTERACTIVE LEGACY SECTION */}
      <section className="about-timeline-premium" style={{ padding: '8rem 0', background: '#0b1f3a', overflow: 'hidden', position: 'relative' }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 6rem' }}>

          <div className="legacy-premium-slider">
            <Swiper
              modules={[Navigation, Autoplay, EffectFade]}
              effect="fade"
              fadeEffect={{ crossFade: true }}
              speed={1000}
              onSlideChange={(swiper) => setActiveTab(swiper.activeIndex)}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              className="premium-swiper"
              allowTouchMove={true}
              autoplay={{ delay: 6000, disableOnInteraction: false }}
            >
              {timelineData.map((item, index) => (
                <SwiperSlide key={index} style={{ background: 'transparent' }}>
                  <div className="premium-slide-content">
                    <div className="slide-left-side">
                      <span className="slide-subtitle">{item.subtitle}</span>
                      <h1 className="outline-year notranslate" translate="no">{item.year}</h1>
                      <p className="slide-desc">{item.desc}</p>
                      <div className="slide-nav-btns">
                        <button className="nav-btn-circ prev-btn" onClick={() => swiperRef.current?.slidePrev()}>
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button className="nav-btn-circ next-btn" onClick={() => swiperRef.current?.slideNext()}>
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    </div>
                    <div className="slide-right-side">
                      <div className="overlap-images">
                        <img src={item.backImg} alt="Layer 1" className="img-back" />
                        <img src={item.frontImg} alt="Layer 2" className="img-front shadow-lg" />
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* PROGRESS NAVIGATION BAR */}
          <div className="timeline-nav-bar">
            <div className="nav-line">
              <div
                className="nav-line-progress"
                style={{ width: `${(activeTab / (timelineData.length - 1)) * 100}%` }}
              ></div>
            </div>
            <div className="nav-step-container">
              {timelineData.map((item, index) => (
                <div
                  key={index}
                  className={`nav-step ${activeTab === index ? 'active' : ''}`}
                  onClick={() => handleTabClick(index)}
                >
                  <div className="step-circle notranslate" translate="no">{item.year}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <div className="molten-divider-wrapper" style={{ background: '#0b1f3a' }}>
        <svg className="molten-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <defs>
            <linearGradient id="liquid-silver-rev" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0b1f3a" />
              <stop offset="50%" stopColor="#dbeafe" />
              <stop offset="100%" stopColor="var(--bg-secondary)" />
            </linearGradient>
          </defs>
          <path
            fill="url(#liquid-silver-rev)"
            d="M0,160 C320,300,420,40,720,160 C1020,280,1120,60,1440,200 L1440,320 L0,320 Z"
          ></path>
        </svg>
      </div>

      {/* Vision & Mission Split */}
      <section className="vision-mission-section" style={{ padding: '8rem 0', background: 'var(--bg-secondary)', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 6rem' }}>
          <div className="vision-mission-row" style={{ display: 'flex', gap: '8rem', alignItems: 'center', marginBottom: '10rem' }}>
            <div className="vm-content animate-hidden animate-left" style={{ flex: 1 }}>
              <h3 className="section-title" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '2rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(10, 102, 255, 0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--border-color)'
                }}>
                  <FontAwesomeIcon icon={faBullseye} style={{ color: 'var(--accent-color)', fontSize: '1.8rem' }} />
                </div>
                Our Mission
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                {[
                  "Provide High Quality, Affordable Jewellery Tools And Machinery.",
                  "Create And Cultivate Long-Term Relationship With Our Esteemed Clients.",
                  "Respond Immediately To The Changing Needs Of Our Customers.",
                  "Achieve Complete Customer Satisfaction.",
                  "Improve our Service Continuously."
                ].map((text, i) => (
                  <div key={i} className={`animate-hidden delay-${(i + 1) * 100}`} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{
                      minWidth: '24px',
                      height: '24px',
                      backgroundColor: 'var(--accent-color)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: '0.2rem'
                    }}>
                      <FontAwesomeIcon icon={faChevronRight} style={{ color: '#fff', fontSize: '0.7rem' }} />
                    </div>
                    <p className="about-description" style={{ color: 'var(--text-main)', margin: 0 }}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="vm-image animate-hidden animate-right" style={{ flex: 1.2, position: 'relative' }}>
              {/* Corner Decorative Circles */}
              <div style={{
                position: 'absolute',
                bottom: '-40px',
                left: '-40px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'rgba(10, 102, 255, 0.15)',
                zIndex: 0,
                pointerEvents: 'none'
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-15px',
                left: '-15px',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: 'var(--accent-color)',
                zIndex: 0,
                opacity: 0.8,
                pointerEvents: 'none'
              }}></div>
              <img src={missionPhoto} alt="Mission" style={{ width: '100%', borderRadius: '12px', position: 'relative', zIndex: 1 }} />
            </div>
          </div>

          <div className="vision-mission-row" style={{ display: 'flex', gap: '8rem', alignItems: 'center', flexDirection: 'row-reverse' }}>
            <div className="vm-content animate-hidden animate-right" style={{ flex: 1 }}>
              <h3 className="section-title" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '2rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(10, 102, 255, 0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--border-color)'
                }}>
                  <FontAwesomeIcon icon={faEye} style={{ color: 'var(--accent-color)', fontSize: '1.8rem' }} />
                </div>
                Our Vision
              </h3>
              <div className="animate-hidden delay-200" style={{
                padding: '2rem',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)'
              }}>
                <p className="about-description" style={{ color: 'var(--text-main)', margin: 0, fontSize: '1.1rem', fontStyle: 'italic', lineHeight: '1.8' }}>
                  "To become the most trusted and recognized name in the jewellery tools and machinery industry across India and beyond, setting new benchmarks in precision and industrial excellence."
                </p>
              </div>
            </div>
            <div className="vm-image animate-hidden animate-left" style={{ flex: 1.2, position: 'relative' }}>
              {/* Corner Decorative Circles */}
              <div style={{
                position: 'absolute',
                bottom: '-40px',
                left: '-40px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'rgba(10, 102, 255, 0.15)',
                zIndex: 0,
                pointerEvents: 'none'
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-15px',
                left: '-15px',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: 'var(--accent-color)',
                zIndex: 0,
                opacity: 0.8,
                pointerEvents: 'none'
              }}></div>
              <img src={visionPhoto} alt="Vision" style={{ width: '100%', borderRadius: '12px', position: 'relative', zIndex: 1 }} />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}